import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ForumIcon from "@mui/icons-material/Forum";
import { keyframes } from "@emotion/react";
import { Box, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import history from "../../utils/history";
import classes from "./AboutVedaz.styles";
import AboutApp from "./components/AboutApp";
import AnalysisOverview from "./components/AnalysisOverview";
import AppFunctions from "./components/AppFunctions";
import DestinyAspect from "./components/DestinyAspect";
import EmotionalAspect from "./components/EmotionalAspect";
import ForWhom from "./components/ForWhom";
import GeneticAspect from "./components/GeneticAspect";
import PhysicalAspect from "./components/PhysicalAspect";
import SoulPathAspect from "./components/SoulPathAspect";
import WhyVedazLove from "./components/WhyVedazLove";

const tabs = [
  { labelKey: "about_nav_whyVedaz" },
  { labelKey: "about_nav_about" },
  { labelKey: "about_nav_functions" },
  { labelKey: "about_nav_forWhom" },
  { labelKey: "about_nav_analysis" },
  { labelKey: "about_nav_physical" },
  { labelKey: "about_nav_emotional" },
  { labelKey: "about_nav_genetic" },
  { labelKey: "about_nav_soulPath" },
  { labelKey: "about_nav_destiny" },
];

// Group 1: pages 1-2 · Group 2: page 3 · Group 3: page 4 · Group 4: pages 5-10
const groups = [
  { labelKey: "about_group_intro", chapters: [0, 1] },
  { labelKey: "about_group_features", chapters: [2] },
  { labelKey: "about_group_audience", chapters: [3] },
  { labelKey: "about_group_analysis", chapters: [4, 5, 6, 7, 8, 9] },
];

const AboutVedaz = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (index: number) => {
    if (index === activeTab) return;
    setActiveTab(index);
  };

  const activeGroupIndex = groups.findIndex((g) =>
    g.chapters.includes(activeTab),
  );
  const activeGroup = groups[activeGroupIndex];
  const isMultiChapterGroup = activeGroup.chapters.length > 1;
  const progressPct = Math.round((activeTab / (tabs.length - 1)) * 100);

  const renderContent = () => {
    switch (activeTab) {
      case 0:
        return <WhyVedazLove chapterIndex={1} />;
      case 1:
        return <AboutApp chapterIndex={2} />;
      case 2:
        return <AppFunctions chapterIndex={3} />;
      case 3:
        return <ForWhom chapterIndex={4} />;
      case 4:
        return (
          <AnalysisOverview
            chapterIndex={5}
            onDimensionClick={handleTabChange}
          />
        );
      case 5:
        return <PhysicalAspect chapterIndex={6} />;
      case 6:
        return <EmotionalAspect chapterIndex={7} />;
      case 7:
        return <GeneticAspect chapterIndex={8} />;
      case 8:
        return <SoulPathAspect chapterIndex={9} />;
      case 9:
        return <DestinyAspect chapterIndex={10} />;
      default:
        return <WhyVedazLove chapterIndex={1} />;
    }
  };

  // ── Navigation bar ─────────────────────────────────────────────
  const renderNav = () => (
    <Box sx={classes.navWrapper}>
      {/* Main row */}
      <Box sx={classes.navMainRow}>
        {/* ← Back */}
        <Box sx={classes.backBtn} onClick={() => history.goBack()}>
          <ArrowBackIcon sx={{ fontSize: "14px" }} />
          <Typography sx={classes.backBtnText}>Back</Typography>
        </Box>

        {/* Separator */}
        <Box sx={classes.navSep} />

        {/* 4 group chips */}
        {groups.map((group, gIdx) => {
          const isActive = gIdx === activeGroupIndex;
          const isMulti = group.chapters.length > 1;
          return (
            <Box
              key={gIdx}
              sx={isActive ? classes.groupChipActive : classes.groupChip}
              onClick={() => handleTabChange(group.chapters[0])}
            >
              <Typography
                sx={
                  isActive ? classes.groupChipNumActive : classes.groupChipNum
                }
              >
                {String(gIdx + 1).padStart(2, "0")}
              </Typography>
              <Typography
                sx={
                  isActive ? classes.groupChipNameActive : classes.groupChipName
                }
              >
                {t(group.labelKey)}
              </Typography>
              {isMulti && (
                <Typography
                  sx={
                    isActive ? classes.groupChipBadge : classes.groupChipCount
                  }
                >
                  {group.chapters.length}
                </Typography>
              )}
            </Box>
          );
        })}

        {/* Push counter + arrows right */}
        <Box sx={classes.navSpacer} />

        {/* X / 10 */}
        <Typography sx={classes.chapterCounter}>
          {activeTab + 1} / {tabs.length}
        </Typography>

        {/* Prev */}
        <Box
          sx={activeTab === 0 ? classes.navArrowDisabled : classes.navArrow}
          onClick={() => activeTab > 0 && handleTabChange(activeTab - 1)}
        >
          <ChevronLeftIcon sx={{ fontSize: "16px" }} />
        </Box>

        {/* Next */}
        <Box
          sx={
            activeTab === tabs.length - 1
              ? classes.navArrowDisabled
              : classes.navArrow
          }
          onClick={() =>
            activeTab < tabs.length - 1 && handleTabChange(activeTab + 1)
          }
        >
          <ChevronRightIcon sx={{ fontSize: "16px" }} />
        </Box>
      </Box>

      {/* Chapter sub-row — only for multi-chapter groups */}
      {isMultiChapterGroup && (
        <Box sx={classes.chapterSubRow}>
          {activeGroup.chapters.map((chapterIndex) => (
            <Box
              key={chapterIndex}
              sx={
                activeTab === chapterIndex
                  ? classes.chapterPillActive
                  : classes.chapterPill
              }
              onClick={() => handleTabChange(chapterIndex)}
            >
              {String(chapterIndex + 1).padStart(2, "0")} &nbsp;
              {t(tabs[chapterIndex].labelKey)}
            </Box>
          ))}
        </Box>
      )}

      {/* Reading progress bar */}
      <Box
        sx={{
          ...classes.navProgressBar,
          background: `linear-gradient(to right, #9053F6 ${progressPct}%, rgba(255,255,255,0.05) ${progressPct}%)`,
        }}
      />
    </Box>
  );

  // ── Next Chapter footer ─────────────────────────────────────────
  const renderNextChapter = () => {
    if (activeTab >= tabs.length - 1) {
      return (
        <Box
          sx={{
            mt: 6,
            mx: { xs: 2, sm: 4, md: 6 },
            mb: 4,
            position: "relative",
            overflow: "hidden",
            borderRadius: "24px",
            background:
              "linear-gradient(135deg, rgba(144,83,246,0.18) 0%, rgba(75,1,80,0.22) 50%, rgba(144,83,246,0.12) 100%)",
            border: "1px solid rgba(144,83,246,0.35)",
            boxShadow:
              "0 0 60px rgba(144,83,246,0.15), 0 0 120px rgba(144,83,246,0.07)",
            cursor: "pointer",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow:
                "0 0 80px rgba(144,83,246,0.25), 0 0 160px rgba(144,83,246,0.12)",
            },
          }}
          onClick={() => history.push("/vedaz-talk")}
        >
          {/* Ambient glow blobs */}
          <Box
            sx={{
              position: "absolute",
              top: "-60px",
              left: "-60px",
              width: "250px",
              height: "250px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(144,83,246,0.22) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "-40px",
              right: "-40px",
              width: "200px",
              height: "200px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(196,156,248,0.16) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          {/* Content */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 3,
              px: { xs: 3, sm: 5 },
              py: { xs: 4, sm: 5 },
            }}
          >
            <Box>
              {/* Tag */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "50px",
                  background: "rgba(144,83,246,0.2)",
                  border: "1px solid rgba(144,83,246,0.4)",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#c49cf8",
                    boxShadow: "0 0 6px rgba(196,156,248,0.9)",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "rgba(196,156,248,0.95)",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                  }}
                >
                  {t("about_cta_tag")}
                </Typography>
              </Box>

              {/* Heading */}
              <Typography
                sx={{
                  fontSize: { xs: "22px", sm: "28px", md: "32px" },
                  fontWeight: 800,
                  background:
                    "linear-gradient(120deg, #FFFFFF 0%, #c49cf8 50%, #9053F6 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  lineHeight: 1.2,
                  mb: 1.5,
                }}
              >
                {t("about_cta_heading")}
              </Typography>

              {/* Subtitle */}
              <Typography
                sx={{
                  fontSize: { xs: "13px", sm: "14px" },
                  color: "rgba(255,255,255,0.55)",
                  lineHeight: 1.7,
                  maxWidth: "520px",
                }}
              >
                {t("about_cta_subtitle")}
              </Typography>
            </Box>

            {/* CTA Button */}
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: { xs: 3, sm: 4 },
                py: { xs: 1.5, sm: 2 },
                borderRadius: "14px",
                background:
                  "linear-gradient(135deg, #9053F6 0%, #6B3AC7 100%)",
                boxShadow:
                  "0 8px 32px rgba(144,83,246,0.45), 0 0 0 1px rgba(144,83,246,0.3)",
                whiteSpace: "nowrap",
                transition: "box-shadow 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  boxShadow:
                    "0 12px 40px rgba(144,83,246,0.6), 0 0 0 1px rgba(196,156,248,0.4)",
                  transform: "scale(1.03)",
                },
              }}
            >
              <ForumIcon sx={{ color: "#fff", fontSize: "20px" }} />
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: "0.3px",
                }}
              >
                {t("about_cta_btn")}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    const next = activeTab + 1;
    return (
      <Box sx={classes.nextChapterCard} onClick={() => handleTabChange(next)}>
        <Box>
          <Typography sx={classes.nextLabel}>
            {t("about_next_chapter")}
          </Typography>
          <Typography sx={classes.nextTitle}>
            {t(tabs[next].labelKey)}
          </Typography>
        </Box>
        <ArrowForwardIcon sx={classes.nextArrow} />
      </Box>
    );
  };

  return (
    <Box sx={classes.pageContainer}>
      {renderNav()}

      <Box sx={classes.contentArea}>
        <Box
          sx={{
            ...classes.contentInner,
            opacity: 1,
            transform: "translateY(0px)",
            transition: "opacity 0.16s ease, transform 0.16s ease",
          }}
        >
          {renderContent()}
          {renderNextChapter()}
        </Box>
      </Box>
    </Box>
  );
};

export default AboutVedaz;
